import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { ArrowRight, MessageSquarePlus, RefreshCw, Send, Ticket as TicketIcon } from 'lucide-react';
import { toast } from 'sonner';
import {
    supportAPI,
    type Ticket,
    type TicketCategory,
    type TicketPriority,
} from '@/services/api';

/**
 * تیکت‌های پشتیبانی — نسخه‌ی پنل مشتری.
 *
 * دوقلوی همین فایل در پنل نماینده است. کد یکی است ولی عمداً کپی شده و به
 * پکیج مشترک منتقل نشده: دو اپ مستقل با build و مسیر API جدا هستند و
 * `@/services/api` در هرکدام به سرویس خودش اشاره می‌کند.
 *
 * سه نما در یک کامپوننت: فهرست، فرم تیکت جدید و گفتگو. جدا نکردنشان عمدی
 * است؛ کاربر بین این سه مدام رفت‌وبرگشت دارد و مسیر جدا یعنی از دست دادن
 * حالت فهرست در هر بازگشت.
 */

const CATEGORIES: { value: TicketCategory; label: string }[] = [
    { value: 'technical', label: 'فنی' },
    { value: 'financial', label: 'مالی' },
    { value: 'license', label: 'لایسنس' },
    { value: 'account', label: 'حساب کاربری' },
    { value: 'other', label: 'سایر' },
];

const PRIORITIES: { value: TicketPriority; label: string }[] = [
    { value: 'low', label: 'کم' },
    { value: 'normal', label: 'عادی' },
    { value: 'high', label: 'زیاد' },
    { value: 'urgent', label: 'فوری' },
];

const STATUS_STYLE: Record<string, string> = {
    open: 'bg-amber-100 text-amber-800 border-amber-200',
    pending: 'bg-sky-100 text-sky-800 border-sky-200',
    answered: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    closed: 'bg-gray-100 text-gray-600 border-gray-200',
};

const PRIORITY_STYLE: Record<string, string> = {
    urgent: 'bg-red-100 text-red-800 border-red-200',
    high: 'bg-orange-100 text-orange-800 border-orange-200',
    normal: 'bg-slate-100 text-slate-700 border-slate-200',
    low: 'bg-slate-100 text-slate-500 border-slate-200',
};

const faDateTime = (value?: string | null) =>
    value
        ? new Date(value).toLocaleString('fa-IR', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
          })
        : '—';

const SupportTickets: React.FC = () => {
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState<'list' | 'new' | 'thread'>('list');
    const [active, setActive] = useState<Ticket | null>(null);

    // فرم تیکت جدید
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [category, setCategory] = useState<TicketCategory>('other');
    const [priority, setPriority] = useState<TicketPriority>('normal');
    const [submitting, setSubmitting] = useState(false);

    // پاسخ در گفتگو
    const [reply, setReply] = useState('');
    const [sending, setSending] = useState(false);

    const loadTickets = useCallback(async () => {
        setLoading(true);
        try {
            const data = await supportAPI.list({ pageSize: 50 });
            setTickets(data.tickets ?? []);
        } catch (error: any) {
            toast.error(error?.message || 'خطا در دریافت تیکت‌ها');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadTickets();
    }, [loadTickets]);

    const openThread = async (ticket: Ticket) => {
        setView('thread');
        setActive(ticket);
        setReply('');
        try {
            setActive(await supportAPI.getById(ticket.id));
        } catch (error: any) {
            toast.error(error?.message || 'خطا در دریافت گفتگو');
            setView('list');
        }
    };

    const resetForm = () => {
        setSubject('');
        setBody('');
        setCategory('other');
        setPriority('normal');
    };

    const submitTicket = async () => {
        if (subject.trim().length < 3) return toast.error('موضوع تیکت را بنویسید');
        if (body.trim().length < 5) return toast.error('متن پیام را بنویسید');

        setSubmitting(true);
        try {
            const ticket = await supportAPI.create({
                subject: subject.trim(),
                body: body.trim(),
                category,
                priority,
            });
            toast.success(`تیکت ${ticket.ticket_no} ثبت شد`);
            resetForm();
            await loadTickets();
            openThread(ticket);
        } catch (error: any) {
            toast.error(error?.message || 'خطا در ثبت تیکت');
        } finally {
            setSubmitting(false);
        }
    };

    const sendReply = async () => {
        if (!active || !reply.trim()) return;

        setSending(true);
        try {
            setActive(await supportAPI.reply(active.id, reply.trim()));
            setReply('');
            loadTickets();
        } catch (error: any) {
            toast.error(error?.message || 'خطا در ارسال پاسخ');
        } finally {
            setSending(false);
        }
    };

    const closeTicket = async () => {
        if (!active) return;
        try {
            setActive(await supportAPI.close(active.id));
            toast.success('تیکت بسته شد');
            loadTickets();
        } catch (error: any) {
            toast.error(error?.message || 'خطا در بستن تیکت');
        }
    };

    /* ------------------------------------------------------------ گفتگو */

    if (view === 'thread' && active) {
        const isClosed = active.status === 'closed';

        return (
            <Card className="border-green-200 shadow-sm">
                <CardHeader className="border-b border-green-100">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                                <span dir="ltr" className="font-mono text-xs text-green-700">
                                    {active.ticket_no}
                                </span>
                                <Badge variant="outline" className={STATUS_STYLE[active.status]}>
                                    {active.status_label}
                                </Badge>
                                <Badge variant="outline" className={PRIORITY_STYLE[active.priority]}>
                                    {active.priority_label}
                                </Badge>
                            </div>
                            <CardTitle className="mt-2 text-green-900">{active.subject}</CardTitle>
                            <CardDescription className="text-green-700">
                                {active.category_label} · {faDateTime(active.created_at)}
                            </CardDescription>
                        </div>

                        <Button
                            variant="outline"
                            className="border-green-300 text-green-700 hover:bg-green-50"
                            onClick={() => {
                                setView('list');
                                setActive(null);
                            }}
                        >
                            <ArrowRight className="ml-2 h-4 w-4" />
                            بازگشت
                        </Button>
                    </div>
                </CardHeader>

                <CardContent className="space-y-4 pt-5">
                    {/* پیام‌ها */}
                    <div className="space-y-3">
                        {(active.messages ?? []).map((message) => {
                            const fromSupport = message.sender_type === 'admin';
                            const isSystem = message.sender_type === 'system';

                            return (
                                <div
                                    key={message.id}
                                    className={`flex ${fromSupport ? 'justify-start' : 'justify-end'}`}
                                >
                                    <div
                                        className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-7 ${
                                            isSystem
                                                ? 'bg-gray-100 text-gray-600'
                                                : fromSupport
                                                  ? 'bg-green-600 text-white'
                                                  : 'border border-green-200 bg-white text-gray-800'
                                        }`}
                                    >
                                        <p className="mb-1 text-xs opacity-80">{message.display_name}</p>
                                        <p className="whitespace-pre-wrap">{message.body}</p>
                                        <p className="mt-2 text-[11px] opacity-70">
                                            {faDateTime(message.created_at)}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* پاسخ */}
                    {isClosed ? (
                        <div className="rounded-lg bg-gray-50 p-4 text-center text-sm text-gray-600">
                            این تیکت بسته شده است. برای پیگیری، تیکت جدید باز کنید.
                        </div>
                    ) : (
                        <div className="space-y-3 border-t border-green-100 pt-4">
                            <Textarea
                                value={reply}
                                onChange={(e) => setReply(e.target.value)}
                                placeholder="پاسخ خود را بنویسید…"
                                rows={3}
                                className="resize-none border-green-200"
                            />
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <Button
                                    variant="outline"
                                    className="border-gray-300 text-gray-600 hover:bg-gray-50"
                                    onClick={closeTicket}
                                >
                                    بستن تیکت
                                </Button>
                                <Button
                                    className="bg-green-600 hover:bg-green-700"
                                    disabled={sending || !reply.trim()}
                                    onClick={sendReply}
                                >
                                    <Send className="ml-2 h-4 w-4" />
                                    {sending ? 'در حال ارسال…' : 'ارسال پاسخ'}
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        );
    }

    /* --------------------------------------------------------- تیکت جدید */

    if (view === 'new') {
        return (
            <Card className="border-green-200 shadow-sm">
                <CardHeader className="border-b border-green-100">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <CardTitle className="text-green-900">تیکت جدید</CardTitle>
                            <CardDescription className="text-green-700">
                                مشکل یا سؤال خود را بنویسید؛ پاسخ در همین صفحه نمایش داده می‌شود.
                            </CardDescription>
                        </div>
                        <Button
                            variant="outline"
                            className="border-green-300 text-green-700 hover:bg-green-50"
                            onClick={() => setView('list')}
                        >
                            <ArrowRight className="ml-2 h-4 w-4" />
                            بازگشت
                        </Button>
                    </div>
                </CardHeader>

                <CardContent className="space-y-4 pt-5">
                    <div>
                        <label className="mb-1.5 block text-sm text-green-900">موضوع</label>
                        <Input
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="مثلاً: خطا هنگام تخصیص لایسنس"
                            className="border-green-200"
                        />
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                            <label className="mb-1.5 block text-sm text-green-900">دسته‌بندی</label>
                            <Select value={category} onValueChange={(v) => setCategory(v as TicketCategory)}>
                                <SelectTrigger className="border-green-200">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {CATEGORIES.map((item) => (
                                        <SelectItem key={item.value} value={item.value}>
                                            {item.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <label className="mb-1.5 block text-sm text-green-900">اولویت</label>
                            <Select value={priority} onValueChange={(v) => setPriority(v as TicketPriority)}>
                                <SelectTrigger className="border-green-200">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {PRIORITIES.map((item) => (
                                        <SelectItem key={item.value} value={item.value}>
                                            {item.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div>
                        <label className="mb-1.5 block text-sm text-green-900">شرح</label>
                        <Textarea
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            rows={6}
                            placeholder="هرچه دقیق‌تر بنویسید سریع‌تر حل می‌شود: چه کاری کردید، چه انتظاری داشتید و چه اتفاقی افتاد."
                            className="resize-none border-green-200"
                        />
                    </div>

                    <Button
                        className="w-full bg-green-600 hover:bg-green-700"
                        disabled={submitting}
                        onClick={submitTicket}
                    >
                        {submitting ? 'در حال ثبت…' : 'ثبت تیکت'}
                    </Button>
                </CardContent>
            </Card>
        );
    }

    /* ------------------------------------------------------------ فهرست */

    return (
        <Card className="border-green-200 shadow-sm">
            <CardHeader className="border-b border-green-100">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <CardTitle className="flex items-center gap-2 text-green-900">
                            <TicketIcon className="h-5 w-5" />
                            تیکت‌های پشتیبانی
                        </CardTitle>
                        <CardDescription className="text-green-700">
                            گفتگوی مستقیم با تیم پشتیبانی — سابقه‌ی همه‌ی پیگیری‌ها اینجا می‌ماند
                        </CardDescription>
                    </div>

                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            className="border-green-300 text-green-700 hover:bg-green-50"
                            onClick={loadTickets}
                            disabled={loading}
                        >
                            <RefreshCw className={`ml-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                            به‌روزرسانی
                        </Button>
                        <Button className="bg-green-600 hover:bg-green-700" onClick={() => setView('new')}>
                            <MessageSquarePlus className="ml-2 h-4 w-4" />
                            تیکت جدید
                        </Button>
                    </div>
                </div>
            </CardHeader>

            <CardContent className="pt-5">
                {loading ? (
                    <div className="space-y-3">
                        {[0, 1, 2].map((i) => (
                            <div key={i} className="h-20 animate-pulse rounded-xl bg-green-50" />
                        ))}
                    </div>
                ) : tickets.length === 0 ? (
                    <div className="py-10 text-center">
                        <p className="text-gray-600">هنوز تیکتی ثبت نکرده‌اید.</p>
                        <Button
                            className="mt-4 bg-green-600 hover:bg-green-700"
                            onClick={() => setView('new')}
                        >
                            <MessageSquarePlus className="ml-2 h-4 w-4" />
                            اولین تیکت را بسازید
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {tickets.map((ticket) => (
                            <button
                                key={ticket.id}
                                onClick={() => openThread(ticket)}
                                className="w-full rounded-xl border border-green-100 bg-white p-4 text-right transition hover:border-green-300 hover:bg-green-50/50"
                            >
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span dir="ltr" className="font-mono text-xs text-green-700">
                                            {ticket.ticket_no}
                                        </span>
                                        <Badge variant="outline" className={STATUS_STYLE[ticket.status]}>
                                            {ticket.status_label}
                                        </Badge>
                                        {ticket.priority === 'urgent' && (
                                            <Badge variant="outline" className={PRIORITY_STYLE.urgent}>
                                                فوری
                                            </Badge>
                                        )}
                                    </div>
                                    <span className="text-xs text-gray-500">
                                        {faDateTime(ticket.last_reply_at ?? ticket.created_at)}
                                    </span>
                                </div>

                                <p className="mt-2 font-medium text-gray-900">{ticket.subject}</p>
                                <p className="mt-1 text-xs text-gray-500">
                                    {ticket.category_label} · {ticket.message_count} پیام
                                    {ticket.awaiting_reply && ticket.status !== 'closed' && (
                                        <span className="text-amber-600"> · در انتظار پاسخ پشتیبانی</span>
                                    )}
                                </p>
                            </button>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default SupportTickets;
